if (!customElements.get('product-form')) {
  const DEFAULT_BAND_SIZING_VARIANT_ID = 43579576057892;
  const DEFAULT_EXTRA_BATTERY_VARIANT_ID = 0;
  const ADDON_GROUP_KEY = '_addon_group_id';

  async function tnwsFetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' });
    return response.json();
  }

  function tnwsIsAddonVariant(variantId, bandSizingVariantId, extraBatteryVariantId) {
    const id = Number(variantId);
    return id === Number(bandSizingVariantId) || id === Number(extraBatteryVariantId);
  }

  function tnwsFindParentCartLine(cart, mainVariantId, bandSizingVariantId, extraBatteryVariantId) {
    return cart.items.find(
      (item) =>
        Number(item.variant_id) === Number(mainVariantId) &&
        !tnwsIsAddonVariant(item.variant_id, bandSizingVariantId, extraBatteryVariantId)
    );
  }

  function tnwsGetLineProp(item, key) {
    return String((item.properties || {})[key] || '').trim();
  }

  function tnwsBuildLineProperties(formData, shouldAddBandSizing, addonGroupId) {
    const properties = {};

    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        properties[key.slice(11, -1)] = value;
      }
    }

    if (!shouldAddBandSizing) {
      properties['Wrist Size'] = null;
    }

    properties[ADDON_GROUP_KEY] = addonGroupId;
    return properties;
  }

  async function tnwsCartChange(lineKey, quantity, properties, sectionsConfig) {
    const body = { id: lineKey, quantity };
    if (properties !== undefined) body.properties = properties;

    const config = fetchConfig('javascript');
    config.headers['Content-Type'] = 'application/json';
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    if (sectionsConfig?.sections?.length) {
      body.sections = sectionsConfig.sections;
      body.sections_url = sectionsConfig.sections_url;
    }

    config.body = JSON.stringify(body);
    const response = await fetch(routes.cart_change_url, config);
    return response.json();
  }

  async function tnwsCartAddItems(items, sectionsConfig) {
    const payload = { items };

    if (sectionsConfig?.sections?.length) {
      payload.sections = sectionsConfig.sections;
      payload.sections_url = sectionsConfig.sections_url;
    }

    const config = fetchConfig('javascript');
    config.headers['Content-Type'] = 'application/json';
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(payload);

    const response = await fetch(routes.cart_add_url, config);
    return response.json();
  }

  async function tnwsSyncAddonLine({
    cart,
    variantId,
    addonGroupId,
    shouldInclude,
    quantity,
    mainVariantId,
    addonType,
  }) {
    if (!variantId || variantId <= 0) return cart;

    const existingLine = cart.items.find(
      (item) =>
        Number(item.variant_id) === Number(variantId) &&
        tnwsGetLineProp(item, ADDON_GROUP_KEY) === addonGroupId
    );

    if (shouldInclude) {
      if (existingLine) {
        if (Number(existingLine.quantity) === Number(quantity)) return cart;

        const response = await tnwsCartChange(existingLine.key, quantity, existingLine.properties, null);
        if (response.status) throw new Error(response.description || 'Could not update cart.');
        return response.items ? response : tnwsFetchCart();
      }

      const response = await tnwsCartAddItems(
        [
          {
            id: variantId,
            quantity,
            properties: {
              [ADDON_GROUP_KEY]: addonGroupId,
              _addon_type: addonType,
              _addon_parent_variant_id: `${mainVariantId}`,
            },
          },
        ],
        null
      );
      if (response.status) throw new Error(response.description || 'Could not update cart.');
      return response.items ? response : tnwsFetchCart();
    }

    if (!existingLine) return cart;

    const response = await tnwsCartChange(existingLine.key, 0, existingLine.properties, null);
    if (response.status) throw new Error(response.description || 'Could not update cart.');
    return response.items ? response : tnwsFetchCart();
  }

  async function tnwsPrepareCartRenderResponse(response, updatedParent, sectionsConfig) {
    const prepared = { ...response };
    prepared.key = updatedParent.key;
    prepared.id = updatedParent.variant_id;
    prepared._tnwsCartUpdate = true;

    if (!sectionsConfig?.sections?.length) return prepared;

    const missingSection = sectionsConfig.sections.some((id) => !prepared.sections?.[id]);
    if (!missingSection) return prepared;

    const sectionUrl = `${routes.cart_url}?sections=${encodeURIComponent(
      sectionsConfig.sections.join(',')
    )}&sections_url=${encodeURIComponent(sectionsConfig.sections_url)}`;
    const sectionData = await fetch(sectionUrl).then((res) => res.json());

    prepared.sections = {
      ...(prepared.sections || {}),
      ...sectionData,
    };

    return prepared;
  }

  async function tnwsRefreshCartDrawer(cartDrawer, itemCount) {
    const responseText = await fetch(`${routes.cart_url}?section_id=cart-drawer`).then((res) => res.text());
    const html = new DOMParser().parseFromString(responseText, 'text/html');

    for (const selector of ['cart-drawer-items', '.cart-drawer__footer']) {
      const targetElement = document.querySelector(selector);
      const sourceElement = html.querySelector(selector);
      if (targetElement && sourceElement) {
        targetElement.replaceWith(sourceElement);
      }
    }

    cartDrawer?.classList.toggle('is-empty', itemCount === 0);
    cartDrawer?.querySelector('.drawer__inner')?.classList.toggle('is-empty', itemCount === 0);
  }

  async function tnwsRenderUpdatedCart(cartElement, response) {
    const itemCount = Number(response.item_count || 0);
    const displayCount = window.tnwsGetDisplayCartCount?.(response) ?? Number(response.item_count || 0);
    window.tnwsUpdateCartIconCount?.(displayCount);

    if (!cartElement) return;

    if (cartElement.tagName === 'CART-DRAWER') {
      await tnwsRefreshCartDrawer(cartElement, itemCount);
      setTimeout(() => cartElement.open(), 0);
      return;
    }

    if (typeof cartElement.renderContents === 'function') {
      cartElement.renderContents(response);
    }
  }

  async function tnwsUpdateExistingCartItem({
    formData,
    mainVariantId,
    shouldAddBandSizing,
    shouldAddExtraBattery,
    bandSizingVariantId,
    extraBatteryVariantId,
    sectionsConfig,
  }) {
    let cart = await tnwsFetchCart();
    const parentLine = tnwsFindParentCartLine(cart, mainVariantId, bandSizingVariantId, extraBatteryVariantId);
    if (!parentLine) return null;

    const addonGroupId =
      tnwsGetLineProp(parentLine, ADDON_GROUP_KEY) || `addon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const properties = tnwsBuildLineProperties(formData, shouldAddBandSizing, addonGroupId);
    const parentQuantity = Number(parentLine.quantity || 1);

    let response = await tnwsCartChange(parentLine.key, parentQuantity, properties, null);
    if (response.status) throw new Error(response.description || 'Could not update cart.');

    cart = await tnwsFetchCart();
    let updatedParent = tnwsFindParentCartLine(cart, mainVariantId, bandSizingVariantId, extraBatteryVariantId);
    if (!updatedParent) throw new Error('Could not update cart.');

    const activeGroupId = tnwsGetLineProp(updatedParent, ADDON_GROUP_KEY) || addonGroupId;

    cart = await tnwsSyncAddonLine({
      cart,
      variantId: bandSizingVariantId,
      addonGroupId: activeGroupId,
      shouldInclude: shouldAddBandSizing && bandSizingVariantId > 0,
      quantity: Number(updatedParent.quantity || parentQuantity),
      mainVariantId,
      addonType: 'band_sizing',
    });

    cart = await tnwsSyncAddonLine({
      cart,
      variantId: extraBatteryVariantId,
      addonGroupId: activeGroupId,
      shouldInclude: shouldAddExtraBattery && extraBatteryVariantId > 0,
      quantity: Number(updatedParent.quantity || parentQuantity),
      mainVariantId,
      addonType: 'extra_battery',
    });

    cart = await tnwsFetchCart();
    updatedParent = tnwsFindParentCartLine(cart, mainVariantId, bandSizingVariantId, extraBatteryVariantId);
    if (!updatedParent) throw new Error('Could not update cart.');

    response = await tnwsCartChange(
      updatedParent.key,
      Number(updatedParent.quantity || parentQuantity),
      updatedParent.properties,
      sectionsConfig
    );
    if (response.status) throw new Error(response.description || 'Could not update cart.');

    return tnwsPrepareCartRenderResponse(response, updatedParent, sectionsConfig);
  }

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', (evt) => this.onSubmitHandler(evt));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.buyNowButton = this.querySelector('[data-buy-now]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        if (this.buyNowButton) {
          this.buyNowButton.addEventListener('click', (evt) => {
            this.onSubmitHandler(evt, { goToCheckout: true, activeButton: this.buyNowButton });
          });
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      validateAddons() {
        const yesRadio = this.querySelector('#band-sizing-yes');
        const wristInput = this.querySelector('#wrist-size-input');
        const errorEl = this.querySelector('#wristSizeError');

        if (yesRadio?.checked && wristInput && !String(wristInput.value || '').trim()) {
          if (errorEl) errorEl.style.display = 'block';
          wristInput.focus();
          return false;
        }

        if (errorEl) errorEl.style.display = 'none';
        return true;
      }

      onSubmitHandler(evt, options = {}) {
        evt?.preventDefault();

        const goToCheckout = options.goToCheckout === true;
        const activeButton = options.activeButton || this.submitButton;
        const activeSpinner = activeButton.querySelector('.loading__spinner');

        if (!this.validateAddons()) return;
        if (activeButton.getAttribute('aria-disabled') === 'true' || activeButton.disabled) return;

        this.handleErrorMessage();

        activeButton.setAttribute('aria-disabled', true);
        activeButton.classList.add('loading');
        activeSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const bandSizingYes = this.querySelector('#band-sizing-yes');
        const extraBatteryYes = this.querySelector('#battery-yes');
        const bandSizingVariantId = parseInt(this.dataset.bandSizingVariantId, 10) || DEFAULT_BAND_SIZING_VARIANT_ID;
        const extraBatteryVariantId = parseInt(this.dataset.extraBatteryVariantId, 10) || DEFAULT_EXTRA_BATTERY_VARIANT_ID;
        const shouldAddBandSizing = bandSizingYes && bandSizingYes.checked;
        const shouldAddExtraBattery = extraBatteryYes && extraBatteryYes.checked;
        const mainVariantId = formData.get('id');
        const mainQuantity = parseInt(formData.get('quantity'), 10) || 1;

        let body;
        if (shouldAddBandSizing || shouldAddExtraBattery) {
          const addonGroupId = `addon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

          // Add main product plus selected add-ons
          const items = [
            {
              id: mainVariantId,
              quantity: mainQuantity,
              properties: {}
            }
          ];

          items[0].properties[ADDON_GROUP_KEY] = addonGroupId;

          if (shouldAddBandSizing && bandSizingVariantId > 0) {
            items.push({
              id: bandSizingVariantId,
              quantity: mainQuantity,
              properties: {
                [ADDON_GROUP_KEY]: addonGroupId,
                _addon_type: 'band_sizing',
                _addon_parent_variant_id: `${mainVariantId}`
              }
            });
          }

          if (shouldAddExtraBattery && extraBatteryVariantId > 0) {
            items.push({
              id: extraBatteryVariantId,
              quantity: mainQuantity,
              properties: {
                [ADDON_GROUP_KEY]: addonGroupId,
                _addon_type: 'extra_battery',
                _addon_parent_variant_id: `${mainVariantId}`
              }
            });
          }

          // Extract properties from formData
          for (let [key, value] of formData.entries()) {
            if (key.startsWith('properties[')) {
              const propKey = key.slice(11, -1);
              items[0].properties[propKey] = value;
            }
          }

          const payload = { items };
          if (this.cart && !goToCheckout) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
          }

          body = JSON.stringify(payload);
          config.headers['Content-Type'] = 'application/json';
        } else {
          body = formData;
        }

        if (this.cart && !goToCheckout) {
          if (body instanceof FormData) {
            body.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            body.append('sections_url', window.location.pathname);
          }
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = body;

        const sectionsConfig =
          this.cart && !goToCheckout
            ? {
                sections: this.cart.getSectionsToRender().map((section) => section.id),
                sections_url: window.location.pathname,
              }
            : null;

        const addToCart = () =>
          fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json());

        const restoreSavedCartOnFailure = () => {
          if (!goToCheckout || !window.TNWSCartRestore) return Promise.resolve();
          return window.TNWSCartRestore.restoreSavedCart();
        };

        const submitCart = goToCheckout
          ? Promise.resolve(window.TNWSCartRestore?.saveCurrentCart?.())
              .then(() =>
                fetch('/cart/clear.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                  },
                })
              )
              .then((response) => response.json())
              .then((clearResponse) => {
                if (clearResponse?.status) {
                  throw new Error(clearResponse.description || 'Could not start checkout.');
                }
                return addToCart();
              })
          : tnwsUpdateExistingCartItem({
              formData,
              mainVariantId,
              shouldAddBandSizing,
              shouldAddExtraBattery,
              bandSizingVariantId,
              extraBatteryVariantId,
              sectionsConfig,
            }).then((updatedResponse) => updatedResponse || addToCart());

        submitCart
          .then((response) => {
            if (response.status) {
              if (goToCheckout) restoreSavedCartOnFailure();

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = activeButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              activeButton.setAttribute('aria-disabled', true);
              const activeButtonText = activeButton.querySelector('span');
              if (activeButtonText) activeButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (goToCheckout) {
              window.location = '/checkout';
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            const isCartUpdate = response._tnwsCartUpdate === true;

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              })
                .then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                })
                .catch((publishError) => {
                  console.error(publishError);
                });
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');

            if (isCartUpdate) {
              tnwsRenderUpdatedCart(this.cart, response).catch((renderError) => {
                console.error(renderError);
              });
            } else if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      try {
                        this.cart.renderContents(response);
                      } catch (renderError) {
                        console.error(renderError);
                      }
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                try {
                  this.cart.renderContents(response);
                } catch (renderError) {
                  console.error(renderError);
                }
                window.tnwsUpdateCartIconCount?.(window.tnwsGetDisplayCartCount?.(response));
              });
            }
          })
          .catch((e) => {
            console.error(e);
            if (goToCheckout) {
              restoreSavedCartOnFailure();
            }
            this.handleErrorMessage(
              e.message ||
                (goToCheckout ? 'Could not start checkout. Please try again.' : 'Could not update your cart. Please try again.')
            );
            this.error = true;
          })
          .finally(() => {
            activeButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) activeButton.removeAttribute('aria-disabled');
            activeSpinner?.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (this.buyNowButton) this.buyNowButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.buyNowButton) this.buyNowButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
